import { FC, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { ArrowLeftOutlined, SearchOutlined } from '@ant-design/icons'
import type { CrmChatProviderKey } from '@/api/types/crm/crmLeadFunnels.types'
import type { IntegrationChannelItem } from '@/api/integrations/integrations.types'
import { CRM_INTEGRATION_CHAT_PROVIDERS } from '@pages/Admin/CRM/components/PhoneIntegrationButtons/integrations'
import { useCrmIntegrationChatAvailability } from '@/hooks/useCrmIntegrationChatAvailability'
import { useWazzupCrmChannels } from '@/components/common/IntegrationChatDock/useWazzupCrmChannels'
import { useWazzupChannelUnreadCounts } from '@/components/common/IntegrationChatDock/useWazzupChannelUnreadCounts'
import { getWazzupChannelTypeLabel } from '@pages/Admin/CRMSettings/tabs/GeneralPage/components/WazzupCrmSettings/lib/wazzupCrmIntegration.helpers'
import { useCrmIntegrationChatsList } from '../../hooks/useCrmIntegrationChatsList'
import { useCrmIntegrationChatsListInfiniteScroll } from '../../hooks/useCrmIntegrationChatsListInfiniteScroll'
import { useCrmIntegrationChatsListBrowserTabVisibleRefetch } from '../../hooks/useCrmIntegrationChatsListBrowserTabVisibleRefetch'
import { useCrmIntegrationChatsListRealtimeRefetch } from '../../hooks/useCrmIntegrationChatsListRealtimeRefetch'
import { useCrmIntegrationTabsUnreadCounts } from '../../hooks/useCrmIntegrationTabsUnreadCounts'
import { useCrmProviderIntegrationInstances } from '../../hooks/useCrmProviderIntegrationInstances'
import { getCrmIntegrationCommunityTabsAriaLabel } from '../../lib/crmIntegrationCommunityTabLabel'
import type { CrmIntegrationChatListSelection } from '../../types/crm-integration-chat.types'
import { sumIntegrationChatUnreadForIds } from '@/components/common/IntegrationChatDock/integrationChatGlobalUnread'
import { formatVkUnreadCountBadge } from '@pages/Admin/CRM/utils/leadFields'
import { buildIntegrationCrmAccessibilityMap } from '@pages/Admin/CRM/utils/crmIntegrationAccess'
import { getCrmIntegrationAccessDeniedTooltip } from '../../lib/crmIntegrationAccessDeniedTooltip'
import { sortIntegrationChatsInaccessibleLast } from '../../lib/crmIntegrationChatsListSort'
import { IcoRemove, CompressIcon } from '@/utils/icons'
import Preloader from '@UI/Preloader'
import { Empty } from 'antd'
import { CRM_REALTIME_LIST_REFETCH_DEBOUNCE_MS } from '@pages/Admin/CRM/utils/crmRealtimeWebSocket'
import CrmIntegrationChatsListItem from './CrmIntegrationChatsListItem'
import CrmIntegrationCommunityTabLock from './CrmIntegrationCommunityTabLock'
import cn from 'classnames'
import classes from '../../styles/crmIntegrationChat.module.css'

type CrmIntegrationChatsListProps = {
  provider: CrmChatProviderKey
  integrationId?: number | null
  /** Wazzup: клиентский фильтр чатов канала. */
  externalChannelId?: string | null
  onIntegrationIdChange?: (integrationId: number | null) => void
  variant?: 'modal' | 'dock' | 'page'
  onDismiss?: () => void
  onCollapse?: () => void
  onBack?: () => void
  onSelectChat: (selection: CrmIntegrationChatListSelection) => void
  /**
   * /chats: после mark-read / открытия уже прочитанного диалога —
   * bump ключа, чтобы сбросить бейджи списка/аккаунтов/каналов.
   */
  unreadSyncKey?: number
  unreadSyncConversationId?: number | null
}

const isWazzupChannelSelectable = (channel: IntegrationChannelItem): boolean => {
  const status = String(channel.status ?? '').toLowerCase()
  if (status === 'inactive') {
    return false
  }
  return String(channel.external_channel_id ?? '').trim().length > 0
}

const getWazzupChannelTabLabel = (channel: IntegrationChannelItem): string => {
  const name = channel.name?.trim()
  if (name) {
    return name
  }
  const typeLabel = getWazzupChannelTypeLabel(String(channel.type ?? ''))
  if (typeLabel && typeLabel !== 'неизвестный') {
    return typeLabel
  }
  const externalId = String(channel.external_channel_id ?? '').trim()
  return externalId ? `Канал ${externalId.slice(0, 8)}` : `Канал #${channel.id}`
}

/** Вкладки каналов: «MAX · bot_name». */
const getWazzupChannelTabLabelWithType = (
  channel: IntegrationChannelItem,
): string => {
  const name = getWazzupChannelTabLabel(channel)
  const typeLabel = getWazzupChannelTypeLabel(String(channel.type ?? ''))
  if (!typeLabel || typeLabel === 'неизвестный') {
    return name
  }
  if (name.toLowerCase() === typeLabel.toLowerCase()) {
    return typeLabel
  }
  return `${typeLabel} · ${name}`
}

const CrmIntegrationChatsList: FC<CrmIntegrationChatsListProps> = ({
  provider,
  integrationId,
  externalChannelId = null,
  onIntegrationIdChange,
  variant = 'modal',
  onDismiss,
  onCollapse,
  onBack,
  onSelectChat,
  unreadSyncKey = 0,
  unreadSyncConversationId = null,
}) => {
  const viewportRef = useRef<HTMLDivElement | null>(null)
  const [searchInput, setSearchInput] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [selectedIntegrationId, setSelectedIntegrationId] = useState<number | null>(
    integrationId ?? null,
  )
  const [selectedWazzupChannelId, setSelectedWazzupChannelId] = useState<string | null>(
    null,
  )
  /** Вкладка «Мои»: manager_id = текущий sales-manager */
  const [isMineTab, setIsMineTab] = useState(false)

  const profile = useSelector(
    (state: { profile?: { profile?: { id?: number | string; role?: string } } }) =>
      state.profile?.profile,
  )
  const currentUserId =
    profile?.id != null && Number.isFinite(Number(profile.id))
      ? Math.floor(Number(profile.id))
      : null
  const isSalesManager = profile?.role === 'sales-manager'
  const showMineTab = isSalesManager && currentUserId != null && currentUserId > 0

  const lockedExternalChannelId = externalChannelId?.trim() || null
  /** /chats и модалка: аккаунт → канал. Док уже передал channel — без этих вкладок. */
  const isWazzupAccountChannelTabs =
    provider === 'wazzup' && lockedExternalChannelId == null

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(searchInput.trim())
    }, CRM_REALTIME_LIST_REFETCH_DEBOUNCE_MS)

    return () => window.clearTimeout(timer)
  }, [searchInput])

  useEffect(() => {
    setSearchInput('')
    setDebouncedSearch('')
    setSelectedWazzupChannelId(null)
    setSelectedIntegrationId(null)
  }, [provider])

  // «Мои» — дефолт при открытии / смене провайдера (только sales-manager).
  useEffect(() => {
    setIsMineTab(showMineTab)
  }, [provider, showMineTab])

  useEffect(() => {
    // Родитель явно задал integrationId (док / сохранённый выбор).
    if (integrationId == null) {
      return
    }
    setSelectedIntegrationId(integrationId)
  }, [integrationId])

  const { providers } = useCrmIntegrationChatAvailability()
  const { integrations: providerIntegrations } = useCrmProviderIntegrationInstances(
    provider,
    true,
  )

  const activeIntegrationId =
    selectedIntegrationId ?? integrationId ?? undefined

  const integrationNameById = useMemo(
    () =>
      Object.fromEntries(
        providerIntegrations.map((item) => [
          item.id,
          item.name.trim() ||
            (provider === 'wazzup' ? `Аккаунт #${item.id}` : `Бот #${item.id}`),
        ]),
      ),
    [provider, providerIntegrations],
  )

  const integrationAccessibleById = useMemo(
    () => buildIntegrationCrmAccessibilityMap(providerIntegrations),
    [providerIntegrations],
  )

  const accessDeniedTooltip = useMemo(
    () => getCrmIntegrationAccessDeniedTooltip(provider),
    [provider],
  )

  const hasMultipleIntegrations =
    !isWazzupAccountChannelTabs &&
    !lockedExternalChannelId &&
    providerIntegrations.length > 1
  const showIntegrationTabs =
    !lockedExternalChannelId && providerIntegrations.length > 0
  const showIntegrationSubtitle = hasMultipleIntegrations && activeIntegrationId == null

  const resolvedActiveIntegrationId = lockedExternalChannelId
    ? integrationId ?? activeIntegrationId ?? undefined
    : isWazzupAccountChannelTabs
      ? activeIntegrationId ??
        (providerIntegrations.length > 0
          ? providerIntegrations[0].id
          : undefined)
      : providerIntegrations.length === 1
        ? providerIntegrations[0].id
        : activeIntegrationId

  // VK/TG/MAX: 1 бот → он; 2+ → «Все», если родитель не передал валидный id.
  // Важно: при догрузке 1→N сбрасываем на «Все» (иначе залипает первый бот).
  const prevIntegrationsCountRef = useRef(0)

  useEffect(() => {
    prevIntegrationsCountRef.current = 0
  }, [provider])

  useEffect(() => {
    if (lockedExternalChannelId || isWazzupAccountChannelTabs) {
      return
    }

    const count = providerIntegrations.length
    const prevCount = prevIntegrationsCountRef.current
    prevIntegrationsCountRef.current = count

    if (count === 0) {
      return
    }

    if (count === 1) {
      const singleIntegrationId = providerIntegrations[0].id
      if (selectedIntegrationId !== singleIntegrationId) {
        setSelectedIntegrationId(singleIntegrationId)
        onIntegrationIdChange?.(singleIntegrationId)
      }
      return
    }

    // Догрузка 0/1 → N: всегда «Все» (сброс автовыбора единственного бота).
    if (prevCount <= 1) {
      if (selectedIntegrationId != null) {
        setSelectedIntegrationId(null)
      }
      if (integrationId != null) {
        onIntegrationIdChange?.(null)
      }
      return
    }

    const parentValid =
      integrationId != null &&
      providerIntegrations.some((item) => item.id === integrationId)

    if (parentValid) {
      if (selectedIntegrationId !== integrationId) {
        setSelectedIntegrationId(integrationId)
      }
      return
    }

    const localValid =
      selectedIntegrationId != null &&
      providerIntegrations.some((item) => item.id === selectedIntegrationId)

    if (!localValid && selectedIntegrationId != null) {
      setSelectedIntegrationId(null)
      onIntegrationIdChange?.(null)
    }
  }, [
    integrationId,
    isWazzupAccountChannelTabs,
    lockedExternalChannelId,
    onIntegrationIdChange,
    providerIntegrations,
    selectedIntegrationId,
  ])

  // Wazzup (/chats, модалка): всегда выбран аккаунт (первый, если нет валидного).
  useEffect(() => {
    if (!isWazzupAccountChannelTabs) {
      return
    }
    if (providerIntegrations.length === 0) {
      return
    }

    const parentValid =
      integrationId != null &&
      providerIntegrations.some((item) => item.id === integrationId)
    const localValid =
      selectedIntegrationId != null &&
      providerIntegrations.some((item) => item.id === selectedIntegrationId)

    if (localValid) {
      return
    }

    const nextId = parentValid
      ? integrationId!
      : providerIntegrations[0].id

    setSelectedIntegrationId(nextId)
    onIntegrationIdChange?.(nextId)
  }, [
    integrationId,
    isWazzupAccountChannelTabs,
    onIntegrationIdChange,
    providerIntegrations,
    selectedIntegrationId,
  ])

  const wazzupChannelsQueryEnabled =
    provider === 'wazzup' &&
    resolvedActiveIntegrationId != null &&
    resolvedActiveIntegrationId > 0

  const { channels: wazzupChannels, isLoading: isWazzupChannelsLoading } =
    useWazzupCrmChannels(
      wazzupChannelsQueryEnabled ? resolvedActiveIntegrationId! : null,
      wazzupChannelsQueryEnabled,
    )

  const selectableWazzupChannels = useMemo(
    () => wazzupChannels.filter(isWazzupChannelSelectable),
    [wazzupChannels],
  )

  const wazzupChannelMetaByExternalId = useMemo(() => {
    const map: Record<
      string,
      { label: string; typeLabel: string | null }
    > = {}
    wazzupChannels.forEach((channel) => {
      const channelId = String(channel.external_channel_id ?? '').trim()
      if (!channelId) {
        return
      }
      const typeLabel = getWazzupChannelTypeLabel(String(channel.type ?? ''))
      map[channelId] = {
        label: getWazzupChannelTabLabel(channel),
        typeLabel:
          typeLabel && typeLabel !== 'неизвестный' ? typeLabel : null,
      }
    })
    return map
  }, [wazzupChannels])

  const { unreadByExternalChannelId, refetch: refetchWazzupChannelUnread } =
    useWazzupChannelUnreadCounts(
      isWazzupAccountChannelTabs && wazzupChannelsQueryEnabled
        ? resolvedActiveIntegrationId!
        : null,
      isWazzupAccountChannelTabs && wazzupChannelsQueryEnabled,
    )

  // Смена аккаунта — снова «Мои» (если доступна), иначе «Все».
  useEffect(() => {
    if (!isWazzupAccountChannelTabs) {
      return
    }
    setSelectedWazzupChannelId(null)
    setIsMineTab(showMineTab)
  }, [isWazzupAccountChannelTabs, resolvedActiveIntegrationId, showMineTab])

  // Если выбранный канал пропал — возвращаемся на «Все» (null = валидный выбор).
  useEffect(() => {
    if (!isWazzupAccountChannelTabs) {
      return
    }
    if (selectedWazzupChannelId == null) {
      return
    }
    if (isWazzupChannelsLoading) {
      return
    }
    if (selectableWazzupChannels.length === 0) {
      setSelectedWazzupChannelId(null)
      return
    }

    const stillValid = selectableWazzupChannels.some(
      (channel) =>
        String(channel.external_channel_id).trim() === selectedWazzupChannelId,
    )
    if (stillValid) {
      return
    }

    setSelectedWazzupChannelId(null)
  }, [
    isWazzupAccountChannelTabs,
    isWazzupChannelsLoading,
    selectableWazzupChannels,
    selectedWazzupChannelId,
  ])

  const effectiveExternalChannelId =
    lockedExternalChannelId ??
    (isWazzupAccountChannelTabs ? selectedWazzupChannelId : null)

  /** Для API предпочтительнее internal channel.id; fallback — external UUID. */
  const apiChannelId = useMemo(() => {
    if (isMineTab) {
      return null
    }

    const externalId = effectiveExternalChannelId?.trim() || null
    if (!externalId) {
      return null
    }

    const matched = selectableWazzupChannels.find(
      (channel) =>
        String(channel.external_channel_id ?? '').trim() === externalId,
    )
    if (matched?.id != null && String(matched.id).trim() !== '') {
      return String(matched.id)
    }

    return externalId
  }, [effectiveExternalChannelId, isMineTab, selectableWazzupChannels])

  const apiManagerId =
    isMineTab && showMineTab && currentUserId != null ? currentUserId : null

  const providerMeta = useMemo(
    () =>
      CRM_INTEGRATION_CHAT_PROVIDERS.find((item) => item.provider === provider),
    [provider],
  )

  const showUnreadBadge = providers[provider]?.canUseLeadChat ?? false
  const showTabUnreadBadges = showUnreadBadge && showIntegrationTabs
  const providerIntegrationIds = useMemo(
    () => providerIntegrations.map((item) => item.id),
    [providerIntegrations],
  )
  const { unreadByIntegrationId, refetchUnread } = useCrmIntegrationTabsUnreadCounts({
    enabled: showTabUnreadBadges,
  })
  const allIntegrationsUnreadLabel = formatVkUnreadCountBadge(
    sumIntegrationChatUnreadForIds(unreadByIntegrationId, providerIntegrationIds),
  )

  const {
    chats,
    loading,
    loadingMore,
    hasMore,
    error,
    loadMore,
    retry,
    silentRetry,
    clearConversationUnread,
  } = useCrmIntegrationChatsList({
    enabled: true,
    provider,
    integrationId: resolvedActiveIntegrationId ?? undefined,
    channelId: apiChannelId,
    managerId: apiManagerId,
    search: debouncedSearch,
    searchFilter: searchInput,
    integrationNameById,
  })

  const isAllIntegrationsTab =
    hasMultipleIntegrations && resolvedActiveIntegrationId == null

  const displayChats = useMemo(() => {
    if (!isAllIntegrationsTab) {
      return chats
    }

    return sortIntegrationChatsInaccessibleLast(chats, integrationAccessibleById)
  }, [chats, integrationAccessibleById, isAllIntegrationsTab])

  useEffect(() => {
    viewportRef.current?.scrollTo({ top: 0 })
  }, [
    debouncedSearch,
    resolvedActiveIntegrationId,
    effectiveExternalChannelId,
    isMineTab,
  ])

  useCrmIntegrationChatsListRealtimeRefetch({
    silentRetry,
    clearConversationUnread,
    onSync: () => {
      refetchUnread()
      refetchWazzupChannelUnread()
    },
  })
  useCrmIntegrationChatsListBrowserTabVisibleRefetch({
    silentRetry,
    onSync: () => {
      refetchUnread()
      refetchWazzupChannelUnread()
    },
  })

  useEffect(() => {
    if (unreadSyncKey <= 0) {
      return
    }

    const conversationId = unreadSyncConversationId
    if (conversationId != null) {
      clearConversationUnread(conversationId)
    }

    refetchUnread()
    refetchWazzupChannelUnread()

    let cancelled = false
    void (async () => {
      await silentRetry()
      if (cancelled || conversationId == null) {
        return
      }
      // Список мог вернуть stale unread — повторно гасим прочитанный диалог.
      clearConversationUnread(conversationId)
      refetchWazzupChannelUnread()
    })()

    return () => {
      cancelled = true
    }
  }, [
    clearConversationUnread,
    refetchUnread,
    refetchWazzupChannelUnread,
    silentRetry,
    unreadSyncConversationId,
    unreadSyncKey,
  ])

  const { handleViewportScroll } = useCrmIntegrationChatsListInfiniteScroll({
    viewportRef,
    chatsCount: displayChats.length,
    hasMore,
    loading,
    loadingMore,
    loadMore,
  })

  const ProviderIcon = providerMeta?.icon
  const listTitle = providerMeta?.label ?? 'Чаты'
  const lockedChannelSubtitle = useMemo(() => {
    if (!lockedExternalChannelId) {
      return null
    }
    const channel = wazzupChannels.find(
      (item) =>
        String(item.external_channel_id ?? '').trim() === lockedExternalChannelId,
    )
    if (!channel) {
      return null
    }
    return getWazzupChannelTabLabelWithType(channel)
  }, [lockedExternalChannelId, wazzupChannels])

  const handleIntegrationTabSelect = (nextIntegrationId: number | null) => {
    setIsMineTab(false)
    setSelectedIntegrationId(nextIntegrationId)
    onIntegrationIdChange?.(nextIntegrationId)
  }

  const handleAllTabSelect = () => {
    setIsMineTab(false)
    if (hasMultipleIntegrations) {
      setSelectedIntegrationId(null)
      onIntegrationIdChange?.(null)
      return
    }

    const singleId = providerIntegrations[0]?.id
    if (singleId != null) {
      setSelectedIntegrationId(singleId)
      onIntegrationIdChange?.(singleId)
    }
  }

  const handleMineTabSelect = () => {
    setIsMineTab(true)
    setSelectedWazzupChannelId(null)
    if (hasMultipleIntegrations) {
      setSelectedIntegrationId(null)
      onIntegrationIdChange?.(null)
      return
    }

    const singleId = providerIntegrations[0]?.id
    if (singleId != null) {
      setSelectedIntegrationId(singleId)
      onIntegrationIdChange?.(singleId)
    }
  }

  const handleWazzupChannelTabSelect = (nextChannelId: string | null) => {
    setIsMineTab(false)
    setSelectedWazzupChannelId(nextChannelId)
  }

  const showIntegrationAllTab = hasMultipleIntegrations || showMineTab
  const isIntegrationAllTabActive =
    !isMineTab &&
    (hasMultipleIntegrations
      ? resolvedActiveIntegrationId == null
      : resolvedActiveIntegrationId != null)
  const isIntegrationMineTabActive = isMineTab && !isWazzupAccountChannelTabs
  const isWazzupAllTabActive =
    isWazzupAccountChannelTabs && !isMineTab && selectedWazzupChannelId == null
  const isWazzupMineTabActive = isWazzupAccountChannelTabs && isMineTab

  const emptyDescription = searchInput.trim()
    ? 'Ничего не найдено'
    : isWazzupAccountChannelTabs && selectableWazzupChannels.length === 0
      ? 'Каналов пока нет'
      : 'Чатов пока нет'

  const resolveItemSubtitle = (
    item: (typeof chats)[number],
  ): string | null => {
    if (provider === 'wazzup') {
      const channelId = String(item.external_channel_id ?? '').trim()
      if (!channelId) {
        return null
      }
      const meta = wazzupChannelMetaByExternalId[channelId]
      const channelLabel = meta?.label ?? channelId
      // На вкладке «Все» — type + имя канала; на конкретном канале — только имя.
      if (
        isWazzupAccountChannelTabs &&
        selectedWazzupChannelId == null &&
        meta?.typeLabel
      ) {
        return `${meta.typeLabel} · ${channelLabel}`
      }
      return channelLabel
    }

    if (showIntegrationSubtitle || providerIntegrations.length > 1) {
      return integrationNameById[item.integration_id] ?? null
    }

    return null
  }

  const handleSelectItem = (item: (typeof chats)[number]) => {
    if (item.crm_access != null && !item.crm_access.is_accessible) {
      return
    }
    if (!integrationAccessibleById[item.integration_id]) {
      return
    }

    onSelectChat({
      conversationId: item.id,
      leadId: item.lead_id,
      integrationId: item.integration_id,
      provider: item.provider,
    })
  }

  const renderAccountTabs = () => (
    <div
      className={classes.communityTabs}
      role="tablist"
      aria-label={getCrmIntegrationCommunityTabsAriaLabel(provider)}
    >
      {showMineTab && !isWazzupAccountChannelTabs ? (
        <button
          type="button"
          role="tab"
          aria-selected={isIntegrationMineTabActive}
          className={cn(classes.communityTab, {
            [classes.communityTabActive]: isIntegrationMineTabActive,
          })}
          onClick={handleMineTabSelect}
        >
          <span className={classes.communityTabName}>Мои</span>
        </button>
      ) : null}
      {showIntegrationAllTab ? (
        <button
          type="button"
          role="tab"
          aria-selected={isIntegrationAllTabActive}
          className={cn(classes.communityTab, {
            [classes.communityTabActive]: isIntegrationAllTabActive,
          })}
          onClick={handleAllTabSelect}
        >
          <span className={classes.communityTabName}>Все</span>
          {hasMultipleIntegrations && allIntegrationsUnreadLabel ? (
            <span className={classes.communityTabBadge} aria-hidden>
              {allIntegrationsUnreadLabel}
            </span>
          ) : null}
        </button>
      ) : null}
      {(hasMultipleIntegrations || !showMineTab) &&
        providerIntegrations.map((item) => {
        const isActive = !isMineTab && resolvedActiveIntegrationId === item.id
        const tabName =
          item.name.trim() ||
          (provider === 'wazzup' ? `Аккаунт #${item.id}` : `Бот #${item.id}`)
        const tabUnreadLabel = formatVkUnreadCountBadge(
          unreadByIntegrationId[item.id] ?? 0,
        )
        const isTabAccessible = integrationAccessibleById[item.id] ?? true

        return (
          <button
            key={item.id}
            type="button"
            role="tab"
            aria-selected={isActive}
            className={cn(classes.communityTab, {
              [classes.communityTabActive]: isActive,
              [classes.communityTabRestricted]: !isTabAccessible,
            })}
            onClick={() => handleIntegrationTabSelect(item.id)}
          >
            <span className={classes.communityTabName}>{tabName}</span>
            {tabUnreadLabel ? (
              <span className={classes.communityTabBadge} aria-hidden>
                {tabUnreadLabel}
              </span>
            ) : null}
            {!isTabAccessible ? (
              <CrmIntegrationCommunityTabLock tooltip={accessDeniedTooltip} />
            ) : null}
          </button>
        )
      })}
    </div>
  )

  const renderWazzupChannelTabs = () => {
    if (!isWazzupAccountChannelTabs || resolvedActiveIntegrationId == null) {
      return null
    }

    const allChannelsUnreadLabel = formatVkUnreadCountBadge(
      selectableWazzupChannels.reduce((sum, channel) => {
        const channelId = String(channel.external_channel_id).trim()
        return sum + (unreadByExternalChannelId[channelId] ?? 0)
      }, 0),
    )

    return (
      <div className={classes.communityTabsGroup}>
        <div className={classes.communityTabsLabel}>Каналы</div>
        <div
          className={classes.communityTabs}
          role="tablist"
          aria-label="Каналы Wazzup"
        >
          {isWazzupChannelsLoading ? (
            <span className={classes.communityTabsLabel}>Загрузка…</span>
          ) : selectableWazzupChannels.length === 0 ? (
            <span className={classes.communityTabsLabel}>Нет каналов</span>
          ) : (
            <>
              {showMineTab ? (
                <button
                  type="button"
                  role="tab"
                  aria-selected={isWazzupMineTabActive}
                  className={cn(classes.communityTab, {
                    [classes.communityTabActive]: isWazzupMineTabActive,
                  })}
                  onClick={handleMineTabSelect}
                >
                  <span className={classes.communityTabName}>Мои</span>
                </button>
              ) : null}
              <button
                type="button"
                role="tab"
                aria-selected={isWazzupAllTabActive}
                className={cn(classes.communityTab, {
                  [classes.communityTabActive]: isWazzupAllTabActive,
                })}
                onClick={() => handleWazzupChannelTabSelect(null)}
              >
                <span className={classes.communityTabName}>Все</span>
                {allChannelsUnreadLabel ? (
                  <span className={classes.communityTabBadge} aria-hidden>
                    {allChannelsUnreadLabel}
                  </span>
                ) : null}
              </button>
              {selectableWazzupChannels.map((channel) => {
                const channelId = String(channel.external_channel_id).trim()
                const isActive =
                  !isMineTab && selectedWazzupChannelId === channelId
                const tabUnreadLabel = formatVkUnreadCountBadge(
                  unreadByExternalChannelId[channelId] ?? 0,
                )
                const typeHint = getWazzupChannelTypeLabel(
                  String(channel.type ?? ''),
                )

                return (
                  <button
                    key={channelId || String(channel.id)}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    title={typeHint !== 'неизвестный' ? typeHint : undefined}
                    className={cn(classes.communityTab, {
                      [classes.communityTabActive]: isActive,
                    })}
                    onClick={() => handleWazzupChannelTabSelect(channelId)}
                  >
                    <span className={classes.communityTabName}>
                      {getWazzupChannelTabLabelWithType(channel)}
                    </span>
                    {tabUnreadLabel ? (
                      <span className={classes.communityTabBadge} aria-hidden>
                        {tabUnreadLabel}
                      </span>
                    ) : null}
                  </button>
                )
              })}
            </>
          )}
        </div>
      </div>
    )
  }

  return (
    <div
      className={cn(classes.root, {
        [classes.rootDock]: variant === 'dock' || variant === 'page',
      })}
    >
      {variant !== 'page' ? (
        <header className={classes.header}>
          <div className={classes.headerMain}>
            {onBack ? (
              <button
                type="button"
                className={classes.backButton}
                aria-label="К выбору интеграций"
                onClick={onBack}
              >
                <ArrowLeftOutlined
                  onPointerEnterCapture={undefined}
                  onPointerLeaveCapture={undefined}
                />
              </button>
            ) : null}
            {ProviderIcon ? (
              <span className={classes.chatsListVkLogo} aria-hidden>
                <ProviderIcon className={classes.chatsListVkLogoIcon} />
              </span>
            ) : null}
            <div className={classes.chatsListTitleBlock}>
              <span className={classes.chatsListTitle}>{listTitle}</span>
              {lockedChannelSubtitle ? (
                <span
                  className={classes.chatsListChannelSubtitle}
                  title={lockedChannelSubtitle}
                >
                  {lockedChannelSubtitle}
                </span>
              ) : null}
            </div>
          </div>
          <div className={classes.headerActions}>
            {variant === 'dock' && onCollapse ? (
              <button
                type="button"
                className={classes.collapseButton}
                aria-label="Свернуть чат"
                onClick={onCollapse}
              >
                <CompressIcon />
              </button>
            ) : null}
            {variant !== 'dock' && onDismiss ? (
              <button
                type="button"
                className={classes.closeButton}
                aria-label="Закрыть"
                onClick={onDismiss}
              >
                <IcoRemove />
              </button>
            ) : null}
          </div>
        </header>
      ) : null}

      {isWazzupAccountChannelTabs && showIntegrationTabs ? (
        <>
          <div className={classes.communityTabsGroup}>
            <div className={classes.communityTabsLabel}>Аккаунты</div>
            {renderAccountTabs()}
          </div>
          {renderWazzupChannelTabs()}
        </>
      ) : showIntegrationTabs ? (
        renderAccountTabs()
      ) : null}

      <div className={classes.chatsListSearch}>
        <SearchOutlined
          className={classes.chatsListSearchIcon}
          onPointerEnterCapture={undefined}
          onPointerLeaveCapture={undefined}
        />
        <input
          type="search"
          className={classes.chatsListSearchInput}
          placeholder="Поиск по имени"
          value={searchInput}
          onChange={(event) => setSearchInput(event.currentTarget.value)}
        />
      </div>

      <div
        ref={viewportRef}
        className={cn('custom-scroll', classes.chatsListViewport)}
        onScroll={handleViewportScroll}
      >
        {loading && chats.length === 0 ? (
          <div className={classes.chatsListState}>
            <Preloader fullScreen="app" />
          </div>
        ) : error ? (
          <div className={classes.chatsListState}>
            <p className={classes.stateMessageError}>
              Не удалось загрузить список чатов
            </p>
            <button
              type="button"
              className={classes.chatsListRetryButton}
              onClick={() => retry()}
            >
              Повторить
            </button>
          </div>
        ) : displayChats.length === 0 ? (
          <div className={classes.chatsListState}>
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={emptyDescription}
            />
          </div>
        ) : (
          <div className={classes.chatsListItems}>
            {displayChats.map((item) => (
              <CrmIntegrationChatsListItem
                key={item.id}
                item={item}
                variant={variant}
                showUnreadBadge={showUnreadBadge}
                isAccessible={
                  integrationAccessibleById[item.integration_id] ?? true
                }
                accessDeniedTooltip={accessDeniedTooltip}
                integrationSubtitle={resolveItemSubtitle(item)}
                onSelect={handleSelectItem}
              />
            ))}
            {loadingMore ? (
              <div className={classes.chatsListLoadMore}>
                <Preloader fullScreen="app" />
              </div>
            ) : null}
          </div>
        )}
      </div>
    </div>
  )
}

export default CrmIntegrationChatsList
